import { useState, useEffect, useCallback, useRef } from 'react';
import { api, GrRewardRuleResponse } from '@/lib/api';

interface UseRewardRulesOptions {
  gameConfigId?: string;
}

interface UseRewardRulesReturn {
  rules: GrRewardRuleResponse[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useRewardRules(options: UseRewardRulesOptions = {}): UseRewardRulesReturn {
  const [rules, setRules] = useState<GrRewardRuleResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchIdRef = useRef(0);

  const fetchRules = useCallback(async () => {
    const id = ++fetchIdRef.current;
    try {
      setLoading(true);
      const result = await api.listGameRewardsRules({
        gameConfigId: options.gameConfigId || undefined,
      });
      if (fetchIdRef.current !== id) return;
      setRules(result.rules);
      setTotal(result.total);
    } catch (err) {
      if (fetchIdRef.current !== id) return;
      setError(err instanceof Error ? err.message : 'Failed to load reward rules');
    } finally {
      if (fetchIdRef.current === id) {
        setLoading(false);
      }
    }
  }, [options.gameConfigId]);

  const refresh = useCallback(() => {
    setError(null);
    fetchRules();
  }, [fetchRules]);

  useEffect(() => {
    refresh();
    return () => {
      fetchIdRef.current++;
    };
  }, [refresh]);

  return { rules, total, loading, error, refresh };
}
